// Error Reporting Service Integration
// This service handles error reporting to various monitoring services

class ErrorReportingService {
  constructor() {
    this.isInitialized = false;
    this.config = {
      enabled: process.env.NODE_ENV === 'production',
      endpoint: process.env.REACT_APP_ERROR_REPORTING_ENDPOINT || '/api/log-error',
      apiKey: process.env.REACT_APP_ERROR_REPORTING_API_KEY,
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0',
      maxRetries: 3,
      retryDelay: 1000
    };

    // Initialize service
    this.init();
  }

  init() {
    if (!this.config.enabled) {
      console.log('Error reporting disabled in development mode');
      return;
    }

    // Setup global error handlers
    this.setupGlobalErrorHandlers();
    this.isInitialized = true;
  }

  setupGlobalErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError({
        type: 'unhandledrejection',
        error: event.reason,
        promise: event.promise,
        timestamp: new Date().toISOString()
      });
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      this.reportError({
        type: 'uncaught',
        error: event.error,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        timestamp: new Date().toISOString()
      });
    });
  }

  async reportError(errorData) {
    if (!this.config.enabled || !this.isInitialized) {
      return;
    }

    try {
      const enrichedErrorData = this.enrichErrorData(errorData);

      await this.sendToEndpoint(enrichedErrorData);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
      // Fallback to console logging
      this.logToConsole(errorData);
    }
  }

  enrichErrorData(errorData) {
    return {
      ...errorData,
      ...this.getContextualData(),
      ...this.getUserData(),
      ...this.getTechnicalData(),
      reportingService: 'tripaider-frontend'
    };
  }

  getContextualData() {
    return {
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      referrer: document.referrer,
      title: document.title,
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language
    };
  }

  getUserData() {
    try {
      // Get user data from Redux store
      const state = window.store?.getState();
      const user = state?.auth?.user;

      return {
        userId: user?.id || 'anonymous',
        userEmail: user?.email || null,
        userName: user?.name || null,
        userRole: user?.role || null,
        isAuthenticated: !!state?.auth?.token,
        sessionId: this.getSessionId()
      };
    } catch (error) {
      return {
        userId: 'anonymous',
        userEmail: null,
        userName: null,
        userRole: null,
        isAuthenticated: false,
        sessionId: this.getSessionId()
      };
    }
  }

  getTechnicalData() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenResolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: window.screen?.colorDepth || 0,
      pixelRatio: window.devicePixelRatio,
      hardwareConcurrency: navigator.hardwareConcurrency,
      memory: navigator.deviceMemory,
      connection: this.getConnectionInfo(),
      performance: this.getPerformanceMetrics(),
      buildVersion: this.config.version,
      environment: this.config.environment
    };
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('errorSessionId');
    if (!sessionId) {
      sessionId = `err_sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('errorSessionId', sessionId);
    }
    return sessionId;
  }

  getConnectionInfo() {
    try {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        return {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        };
      }
    } catch (error) {
      // Connection API not supported
    }
    return null;
  }

  getPerformanceMetrics() {
    try {
      if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        return {
          domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
          loadComplete: timing.loadEventEnd - timing.navigationStart,
          domInteractive: timing.domInteractive - timing.navigationStart,
          firstPaint: this.getFirstPaintTime()
        };
      }
    } catch (error) {
      // Performance API not available
    }
    return null;
  }

  getFirstPaintTime() {
    try {
      const paintEntries = performance.getEntriesByType('paint');
      const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
      return firstPaint ? firstPaint.startTime : null;
    } catch (error) {
      return null;
    }
  }

  async sendToEndpoint(errorData) {
    const payload = {
      ...errorData,
      apiKey: this.config.apiKey,
      timestamp: new Date().toISOString()
    };

    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.config.apiKey
      },
      body: JSON.stringify(payload)
    };

    let retryCount = 0;

    while (retryCount < this.config.maxRetries) {
      try {
        const response = await fetch(this.config.endpoint, options);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
      } catch (error) {
        retryCount++;

        if (retryCount >= this.config.maxRetries) {
          throw error;
        }

        // Exponential backoff
        await this.delay(this.config.retryDelay * Math.pow(2, retryCount - 1));
      }
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  logToConsole(errorData) {
    console.group('🚨 Error Reporting Service');
    console.error('Error Data:', errorData);
    console.groupEnd();
  }

  // Public API methods
  setUser(user) {
    try {
      sessionStorage.setItem('errorUser', JSON.stringify({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role
      }));
    } catch (error) {
      console.error('Failed to set user for error reporting:', error);
    }
  }

  clearUser() {
    try {
      sessionStorage.removeItem('errorUser');
    } catch (error) {
      console.error('Failed to clear user for error reporting:', error);
    }
  }

  addBreadcrumb(breadcrumb) {
    try {
      const breadcrumbs = JSON.parse(sessionStorage.getItem('errorBreadcrumbs') || '[]');
      breadcrumbs.push({
        ...breadcrumb,
        timestamp: new Date().toISOString()
      });

      // Keep only last 50 breadcrumbs
      if (breadcrumbs.length > 50) {
        breadcrumbs.splice(0, breadcrumbs.length - 50);
      }

      sessionStorage.setItem('errorBreadcrumbs', JSON.stringify(breadcrumbs));
    } catch (error) {
      console.error('Failed to add breadcrumb:', error);
    }
  }

  getBreadcrumbs() {
    try {
      return JSON.parse(sessionStorage.getItem('errorBreadcrumbs') || '[]');
    } catch (error) {
      return [];
    }
  }

  // Integration with popular error monitoring services
  initializeSentry(dsn) {
    // Placeholder for Sentry integration
    console.log('Sentry integration would be initialized here with DSN:', dsn);
  }

  initializeLogRocket(appId) {
    // Placeholder for LogRocket integration
    console.log('LogRocket integration would be initialized here with appId:', appId);
  }

  initializeBugsnag(apiKey) {
    // Placeholder for Bugsnag integration
    console.log('Bugsnag integration would be initialized here with apiKey:', apiKey);
  }
}

// Create singleton instance
const errorReportingService = new ErrorReportingService();

export default errorReportingService;
